import { useState, useRef } from "react";
import { Mail, CheckCircle, FolderSearch, Forward, AlertCircle, ChevronDown, ChevronRight, Settings, XCircle } from "lucide-react";
import { SoundSelector } from "./sound-selector";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  useInfiniteQuery,
  useQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { parseApiErrorObject } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import { format, formatDistanceToNow } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/auth-context";
import { UnmatchedEmailParticipantChips, type UnmatchedEmailReviewer } from "./unmatched-email-participant-chips";
import { UnmatchedEmailSettingsDialog } from "./unmatched-email-settings-dialog";
import { MailOutDialog } from "./mail-out-dialog";

interface EmailNotification {
  id: number;
  userId: number;
  emailAccountId: string;
  emailUID: number;
  suggestedProjectId: string | null;
  detectionMethod: string | null;
  isActioned: boolean;
  flagReason: string | null;
  forwardedByFirstName?: string | null;
  adminEscalationReason?: 'manual' | 'all_declined' | 'no_reviewers' | 'settings' | 'historical_pending' | 'historical_uncached' | null;
  createdAt: string;
  emailData: {
    subject: string;
    fromAddress: string;
    fromName: string | null;
    ccAddresses: string[];
    emailDate: string;
    emailBody: string | null;
  };
  project: {
    id: string;
    projectName: string;
    communicationMode?: string | null;
  } | null;
}

interface UnmatchedEmail {
  emailAccountId: string;
  emailUID: number;
  emailData: {
    subject: string;
    fromAddress: string;
    fromName: string | null;
    ccAddresses: string[];
    emailDate: string;
    emailBody: string | null;
  };
  reviewers?: UnmatchedEmailReviewer[];
}

interface UnmatchedEmailPage {
  items: UnmatchedEmail[];
  total: number;
  participating?: boolean;
}

interface UnmatchedEmailCount {
  count: number;
  participating?: boolean;
}

interface EmailReassignResponse {
  status?: 'assigned' | 'pending_approval';
  alreadyHandled?: boolean;
  success?: boolean;
  message?: string;
  error?: string;
  [key: string]: unknown;
}

interface DontKnowEmailResponse {
  status: 'hidden' | 'forwarded_admin';
  [key: string]: unknown;
}

interface IgnoreEmailResponse {
  status: 'ignored';
  [key: string]: unknown;
}

type ReassignEmail =
  | { kind: "personal"; item: EmailNotification }
  | { kind: "unmatched"; item: UnmatchedEmail };

function ProjectSuggestionThumbnail({ src }: { src?: string | null }) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  return (
    <span className="w-16 h-8 shrink-0 overflow-hidden rounded border border-gray-600 bg-gray-800 flex items-center justify-center">
      {src && src !== failedSrc ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          className="w-full h-full object-cover"
          onError={() => setFailedSrc(src)}
          data-testid="project-suggestion-thumbnail"
        />
      ) : (
        <FolderSearch className="h-4 w-4 text-gray-500" aria-hidden="true" />
      )}
    </span>
  );
}

export function EmailNotificationButton() {
  const { toast } = useToast();
  const { user } = useAuth();
  const reactQueryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [selectedReassignEmail, setSelectedReassignEmail] = useState<ReassignEmail | null>(null);
  const [projectSearchQuery, setProjectSearchQuery] = useState("");
  const [expandedEmailIds, setExpandedEmailIds] = useState<Set<number>>(new Set());
  const [expandedUnmatchedIds, setExpandedUnmatchedIds] = useState<Set<string>>(new Set());
  const [hiddenUnmatchedIds, setHiddenUnmatchedIds] = useState<Set<string>>(new Set());
  const [unmatchedSettingsOpen, setUnmatchedSettingsOpen] = useState(false);
  // Guards against a double-click firing two confirms for the same notification
  // before the disabled state re-renders (task #293).
  const inflightConfirmIds = useRef<Set<number>>(new Set());
  const replyAfterConfirmIds = useRef<Set<number>>(new Set());
  const [confirmedReply, setConfirmedReply] = useState<{ pulseId: number; projectId: string } | null>(null);
  // Keep the global ignore mutation single-flight even when repeated clicks
  // arrive before React has rendered the disabled state.
  const inflightIgnoreIds = useRef<Set<number>>(new Set());
  // The shared-pool action is keyed by email identity rather than notification
  // id. Keep its own synchronous guard for overlapping activations; React's
  // pending render is not a sufficient request-level single-flight boundary,
  // and the endpoint intentionally remains idempotent for retries.
  const inflightUnmatchedIgnoreIds = useRef<Set<string>>(new Set());

  // Coalesce post-action cache reconciliation (task #301). Each confirm or
  // unmatched action used to fire several invalidateQueries immediately;
  // a burst produced an N-times refetch storm that piled on top of the very
  // pool pressure we're trying to relieve. Debounce so a burst triggers a
  // SINGLE trailing reconciliation refetch instead.
  const reconcileTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleNotificationsReconcile = () => {
    if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
    reconcileTimerRef.current = setTimeout(() => {
      reconcileTimerRef.current = null;
      // Use the provider's client, not the module singleton. This matters for
      // embedded/test consumers that provide their own QueryClient and keeps
      // the optimistic cache update and trailing read on the same cache.
      reactQueryClient.invalidateQueries({ queryKey: ['/api/email-notifications'] });
      reactQueryClient.invalidateQueries({ queryKey: ['/api/email-notifications/count'] });
      reactQueryClient.invalidateQueries({ queryKey: ['/api/email-notifications/unmatched'] });
      reactQueryClient.invalidateQueries({ queryKey: ['/api/email-notifications/unmatched/count'] });
      reactQueryClient.invalidateQueries({ queryKey: ['/api/notifications/count'] });
    }, 300);
  };

  // Lightweight count query for badge (always enabled for instant updates)
  const { data: countData } = useQuery<{ count: number }>({
    queryKey: ['/api/email-notifications/count'],
    refetchOnWindowFocus: true,
    staleTime: 30000, // 30 seconds - stays fresh without excessive refetches
    refetchInterval: 60000, // Refetch every minute as fallback
  });

  const isAdmin = user?.role === "admin";
  const canViewUnmatched = isAdmin || user?.role === "team";

  // Shared unmatched email counts are deliberately separate from personal
  // notification counts. The server applies the same internal-role and
  // blacklist/access checks to both endpoints.
  const { data: unmatchedCountData } = useQuery<UnmatchedEmailCount>({
    queryKey: ['/api/email-notifications/unmatched/count'],
    refetchOnWindowFocus: true,
    enabled: canViewUnmatched,
    staleTime: 30000,
    refetchInterval: 60000,
  });

  const unactionedCount =
    (countData?.count || 0) +
    (unmatchedCountData?.participating === false ? 0 : (unmatchedCountData?.count || 0));

  // Fetch full email notifications list (only when dropdown is open)
  const {
    data: emailNotifications = [],
    isLoading: isLoadingEmails,
    isError: isEmailNotificationsError,
    refetch: refetchEmailNotifications,
  } = useQuery<EmailNotification[]>({
    queryKey: ['/api/email-notifications', { unactionedOnly: true }],
    queryFn: async () => {
      const response = await fetch('/api/email-notifications?unactionedOnly=true', {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to fetch email notifications');
      }
      return response.json();
    },
    enabled: isOpen, // Only fetch when dropdown is open
  });

  // Shared unmatched emails are loaded only while the menu is open. Infinite
  // pages avoid hiding older backlog behind a hard first-page limit.
  const {
    data: unmatchedPages,
    isLoading: isLoadingUnmatched,
    isError: isUnmatchedError,
    isFetchingNextPage: isFetchingNextUnmatchedPage,
    hasNextPage: hasMoreUnmatched,
    fetchNextPage: fetchNextUnmatchedPage,
    refetch: refetchUnmatched,
  } = useInfiniteQuery<UnmatchedEmailPage>({
    queryKey: ['/api/email-notifications/unmatched'],
    queryFn: async ({ pageParam = 0 }) => {
      const response = await apiRequest(
        'GET',
        `/api/email-notifications/unmatched?limit=50&offset=${pageParam}`,
      );
      return response.json() as Promise<UnmatchedEmailPage>;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((total, page) => total + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    enabled: isOpen && canViewUnmatched,
    staleTime: 30000,
    refetchInterval: isOpen && canViewUnmatched ? 60000 : false,
  });

  const unmatchedEmails = unmatchedPages?.pages.flatMap((page) => page.items) || [];
  const unmatchedTotal =
    unmatchedPages?.pages[unmatchedPages.pages.length - 1]?.total ??
    unmatchedCountData?.count ??
    0;
  const unmatchedParticipating =
    unmatchedPages?.pages[unmatchedPages.pages.length - 1]?.participating ??
    unmatchedCountData?.participating ??
    // Older deployments/tests did not include the field. Keep those rows
    // visible until the server explicitly says this user is not participating.
    true;
  const showUnmatchedSection = canViewUnmatched && (isAdmin || unmatchedParticipating);
  const visibleUnmatchedEmails = unmatchedEmails.filter((email) => {
    const id = `${email.emailAccountId}:${email.emailUID}`;
    if (hiddenUnmatchedIds.has(id)) return false;
    const ownReview = email.reviewers?.find((reviewer) => reviewer.id === user?.id);
    // A declined row is hidden only for the reviewer who declined it. Other
    // reviewers continue to see the declined chip and can answer it.
    return ownReview?.status !== "dont_know";
  });

  // Fetch all projects for reassignment dialog
  const { data: allProjects = [] } = useQuery<Array<{ id: string; projectName: string; thumbnail?: string | null }>>({
    queryKey: ['/api/projects'],
    enabled: reassignDialogOpen, // Only fetch when reassignment dialog is open
  });

  // Email notification mutations
  const confirmEmailMutation = useMutation({
    mutationFn: async (notificationId: number) => {
      const response = await apiRequest('POST', `/api/email-notifications/${notificationId}/confirm`, {});
      if (!response.ok) {
        throw new Error('Failed to confirm email notification');
      }
      const data = await response.json();
      return data;
    },
    // Optimistically remove the notification + decrement counts on click so the
    // popover closes near-instantly; the cache-first server response returns in
    // ~1s and the background worker finishes the Dropbox uploads (task #293).
    onMutate: async (notificationId: number) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: ['/api/email-notifications'] }),
        queryClient.cancelQueries({ queryKey: ['/api/email-notifications/count'] }),
        queryClient.cancelQueries({ queryKey: ['/api/notifications/count'] }),
      ]);

      const previousList = queryClient.getQueryData<EmailNotification[]>(['/api/email-notifications', { unactionedOnly: true }]);
      const previousEmailCount = queryClient.getQueryData<{ count: number }>(['/api/email-notifications/count']);
      const previousTotalCount = queryClient.getQueryData<{ count: number }>(['/api/notifications/count']);

      const wasPresent = !!previousList?.some((n) => n.id === notificationId);

      if (previousList) {
        queryClient.setQueryData<EmailNotification[]>(
          ['/api/email-notifications', { unactionedOnly: true }],
          previousList.filter((n) => n.id !== notificationId)
        );
      }
      // Only decrement counts if the item was actually in the unactioned list,
      // so we don't drive the badge negative on a stale/duplicate click.
      if (wasPresent) {
        if (previousEmailCount) {
          queryClient.setQueryData<{ count: number }>(
            ['/api/email-notifications/count'],
            { count: Math.max(0, (previousEmailCount.count || 0) - 1) }
          );
        }
        if (previousTotalCount) {
          queryClient.setQueryData<{ count: number }>(
            ['/api/notifications/count'],
            { count: Math.max(0, (previousTotalCount.count || 0) - 1) }
          );
        }
      }

      return { previousList, previousEmailCount, previousTotalCount, wasPresent };
    },
    onSuccess: (data: { success: boolean; alreadyHandled?: boolean; message?: string; pulse?: any }, notificationId: number) => {
      // Check response in specific order:
      // 1. First check if alreadyHandled (explicit race condition signal). A
      //    409 "already handled by another user" is returned by the server as a
      //    success — the notification stays removed.
      if (data.alreadyHandled === true) {
        toast({
          title: "Already Handled",
          description: data.message || "This email has already been assigned by another user. Your notification has been removed.",
          variant: "default",
        });
      } 
      // 2. Then check if pulse was created successfully
      else if (data.pulse) {
        if (replyAfterConfirmIds.current.has(notificationId)) {
          setIsOpen(false);
          setConfirmedReply({ pulseId: data.pulse.id, projectId: data.pulse.projectId });
        }
        toast({
          title: "Success",
          description: "Pulse created successfully from email",
        });
        // The confirm created a new pulse server-side. Don't rely on the
        // WebSocket echo to surface it (broadcasts have no replay — a
        // reconnect blip silently drops them): invalidate the tree snapshot
        // and project caches directly, mirroring the manual add-pulse flow.
        queryClient.invalidateQueries({ queryKey: ['/api/tree/snapshot'], exact: false });
        queryClient.invalidateQueries({ queryKey: ['/api/projects'] });
        queryClient.invalidateQueries({ queryKey: ['/api/years'] });
        if (data.pulse.projectId) {
          queryClient.invalidateQueries({
            queryKey: ['/api/projects', data.pulse.projectId, 'pulses', 'paginated'],
          });
        }
      }
      // 3. Fallback - success but no pulse and not explicitly marked as alreadyHandled
      else {
        toast({
          title: "Already Handled",
          description: "This email has already been assigned. Your notification has been removed.",
          variant: "default",
        });
      }
    },
    onError: (error, _notificationId, context) => {
      // A true error — roll the optimistic removal back so the notification
      // reappears for the user to retry.
      if (context?.previousList) {
        queryClient.setQueryData(['/api/email-notifications', { unactionedOnly: true }], context.previousList);
      }
      if (context?.previousEmailCount) {
        queryClient.setQueryData(['/api/email-notifications/count'], context.previousEmailCount);
      }
      if (context?.previousTotalCount) {
        queryClient.setQueryData(['/api/notifications/count'], context.previousTotalCount);
      }
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to confirm email notification",
        variant: "destructive",
      });
    },
    onSettled: (_data, _error, notificationId) => {
      inflightConfirmIds.current.delete(notificationId);
      replyAfterConfirmIds.current.delete(notificationId);
      // Reconcile optimistic cache with server truth via a single debounced
      // refetch so a burst of confirms doesn't fan out into a 3N refetch storm
      // (task #301). The optimistic onMutate already kept the UI correct.
      scheduleNotificationsReconcile();
    },
  });

  const forwardEmailMutation = useMutation({
    mutationFn: async (notificationId: number) => {
      const response = await apiRequest('POST', `/api/email-notifications/${notificationId}/forward`, {});
      if (!response.ok) {
        throw new Error('Failed to forward email');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/email-notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/email-notifications/count'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/count'] });
      toast({
        title: "Success",
        description: "Email forwarded successfully",
      });
    },
    onError: (error: any) => {
      // The server reopens the notification on any unconfirmed forward and
      // explains the consequence (an uncertain outcome may reach the admin
      // twice on retry); show that text instead of a generic message.
      const body = error?.body;
      queryClient.invalidateQueries({ queryKey: ['/api/email-notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/email-notifications/count'] });
      toast({
        title: body?.code === 'EMAIL_FORWARD_UNCERTAIN' ? "Forward not confirmed" : "Error",
        description: (typeof body?.error === 'string' && body.error) || error?.message || "Failed to forward email",
        variant: "destructive",
      });
    },
  });

  const reassignEmailMutation = useMutation({
    mutationFn: async ({ notificationId, projectId }: { notificationId: number; projectId: string }) => {
      const response = await apiRequest('POST', `/api/email-notifications/${notificationId}/reassign`, { projectId });
      if (!response.ok) {
        throw new Error('Failed to reassign email');
      }
      return response.json() as Promise<EmailReassignResponse>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['/api/email-notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/email-notifications/count'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/count'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'], exact: false });
      // Reassign creates a pulse in the target project server-side — refresh
      // the tree directly instead of relying on the WS echo (no replay).
      queryClient.invalidateQueries({ queryKey: ['/api/tree/snapshot'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['/api/projects'] });
      queryClient.invalidateQueries({ queryKey: ['/api/years'] });
      setReassignDialogOpen(false);
      setSelectedReassignEmail(null);
      setProjectSearchQuery("");
      if (data.status === 'pending_approval') {
        toast({
          title: "Sent for approval",
          description: "The email was sent to the project team for approval.",
        });
      } else {
        toast({
          title: "Success",
          description: "Email reassigned successfully",
        });
      }
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to reassign email",
        variant: "destructive",
      });
    },
  });

  const reassignUnmatchedEmailMutation = useMutation({
    mutationFn: async ({
      emailAccountId,
      emailUID,
      projectId,
    }: {
      emailAccountId: string;
      emailUID: number;
      projectId: string;
    }) => {
      const response = await apiRequest(
        'POST',
        '/api/email-notifications/unmatched/reassign',
        { emailAccountId, emailUID, projectId },
      );
      return response.json() as Promise<EmailReassignResponse>;
    },
    onSuccess: (data, variables) => {
      removeUnmatchedEmailFromLocalCaches(variables.emailAccountId, variables.emailUID);
      invalidateUnmatchedEmailQueries();
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'], exact: false });
      // A successful assignment can create a pulse immediately. A pending
      // approval can create it later, but refreshing these snapshots keeps
      // the caller's project tree and pulse lists current in either case.
      queryClient.invalidateQueries({ queryKey: ['/api/tree/snapshot'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['/api/projects'] });
      queryClient.invalidateQueries({ queryKey: ['/api/years'] });

      setReassignDialogOpen(false);
      setSelectedReassignEmail(null);
      setProjectSearchQuery("");

      if (data.status === 'pending_approval') {
        toast({
          title: "Sent for approval",
          description: "The email was sent to the project team for approval.",
        });
      } else {
        toast({
          title: "Email assigned",
          description: "The email was assigned to the project and a pulse was created.",
        });
      }
    },
    onError: (error: unknown, variables) => {
      const { status, body } = parseApiErrorObject(error);
      if (status === 409 && body?.alreadyHandled === true) {
        // Another caller won the assignment race. Treat the stale row as
        // handled, reconcile with server truth, and avoid a misleading
        // generic failure toast.
        removeUnmatchedEmailFromLocalCaches(variables.emailAccountId, variables.emailUID);
        invalidateUnmatchedEmailQueries();
        queryClient.invalidateQueries({ queryKey: ['/api/notifications'], exact: false });
        setReassignDialogOpen(false);
        setSelectedReassignEmail(null);
        setProjectSearchQuery("");
        toast({
          title: "Already assigned",
          description: "This email was already assigned by another user.",
        });
        return;
      }

      // A 409 without alreadyHandled is a valid target-selection error (for
      // example, no eligible internal members). Keep the dialog and source
      // item intact so the caller can pick another project, and surface the
      // server's actionable message instead of misreporting a stale race.
      const serverMessage =
        (body && typeof body === 'object' && (body.error || body.message)) ||
        (error instanceof Error ? error.message : null);
      toast({
        title: "Error",
        description: typeof serverMessage === 'string' && serverMessage
          ? serverMessage
          : "Failed to reassign unmatched email",
        variant: "destructive",
      });
    },
  });

  const invalidateUnmatchedEmailQueries = () => {
    // All unmatched actions share the same trailing reconciliation as confirm.
    // Calling invalidateQueries once per mutation used to turn two rapid
    // actions into ten immediately-started reads (list/count plus the
    // personal and global badges), which was enough to amplify a small pool
    // outage into an app-wide connection timeout storm.
    scheduleNotificationsReconcile();
  };

  const removeUnmatchedEmailFromLocalCaches = (emailAccountId: string, emailUID: number) => {
    const emailKey = `${emailAccountId}:${emailUID}`;
    setHiddenUnmatchedIds((current) => {
      if (current.has(emailKey)) return current;
      return new Set(current).add(emailKey);
    });

    // Do not wait for the reconciliation request to remove a committed row.
    // TanStack keeps the previous data when that background request fails, so
    // updating the infinite-query pages here prevents a transient pool outage
    // from showing the ignored row again.
    let removed = 0;
    reactQueryClient.setQueryData<InfiniteData<UnmatchedEmailPage>>(
      ['/api/email-notifications/unmatched'],
      (current) => {
        if (!current) return current;
        const pages = current.pages.map((page) => {
          let removedFromPage = 0;
          const items = page.items.filter((email) => {
            const matches =
              email.emailAccountId === emailAccountId && email.emailUID === emailUID;
            if (matches) removedFromPage += 1;
            return !matches;
          });
          removed += removedFromPage;
          return { ...page, items };
        });
        return removed > 0
          ? {
            ...current,
            // Every page reports the same global total. Apply the aggregate
            // removal to every loaded page, not only the page containing it.
            pages: pages.map((page) => ({
              ...page,
              total: Math.max(0, page.total - removed),
            })),
          }
          : current;
      },
    );
    // A websocket or a refetch may already have changed the count cache by
    // the time this mutation settles. Never apply an approximate decrement;
    // mark the authoritative count stale and let the server recompute it.
    reactQueryClient.invalidateQueries({
      queryKey: ['/api/email-notifications/unmatched/count'],
      refetchType: 'none',
    });
  };

  const dontKnowEmailMutation = useMutation({
    mutationFn: async (notificationId: number) => {
      const response = await apiRequest(
        'POST',
        `/api/email-notifications/${notificationId}/dont-know`,
        {},
      );
      return response.json() as Promise<DontKnowEmailResponse>;
    },
    onSuccess: (data, notificationId) => {
      // Remove the answered row from the currently rendered list immediately.
      // The invalidation below still reconciles with server truth, but a
      // successful answer should not leave stale personal-match UI visible
      // while that refetch is in flight.
      const personalListKey = ['/api/email-notifications', { unactionedOnly: true }] as const;
      reactQueryClient.setQueryData<EmailNotification[]>(
        personalListKey,
        (current) => current?.filter((notification) => notification.id !== notificationId),
      );
      // The same action is available from the row and from the reassignment
      // dialog. Only close the dialog when it belongs to the notification
      // which was just answered.
      if (
        selectedReassignEmail?.kind === "personal" &&
        selectedReassignEmail.item.id === notificationId
      ) {
        resetReassignDialog();
      }
      // Refresh both the personal queue and the shared pool. A final
      // dont-know answer can transfer responsibility to admins, so all
      // notification counts need to be reconciled as well.
      invalidateUnmatchedEmailQueries();
      toast({
        title: data.status === 'forwarded_admin' ? 'Forwarded to Admin' : 'Response recorded',
        description: data.status === 'forwarded_admin'
          ? 'All selected reviewers have answered. Admins will decide this email.'
          : 'This email is hidden from your email matches.',
      });
    },
    onError: (error: unknown) => {
      const { body } = parseApiErrorObject(error);
      const message =
        (body && typeof body === 'object' && (body.error || body.message)) ||
        (error instanceof Error ? error.message : null) ||
        "Failed to record “I don't know”.";
      toast({
        title: 'Error',
        description: String(message),
        variant: 'destructive',
      });
    },
  });

  const ignoreEmailMutation = useMutation({
    mutationFn: async (notificationId: number) => {
      const response = await apiRequest(
        'POST',
        `/api/email-notifications/${notificationId}/ignore`,
        {},
      );
      return response.json() as Promise<IgnoreEmailResponse>;
    },
    onSuccess: (_data, notificationId) => {
      // The ignore endpoint is a global final action. Remove the row from the
      // active personal queue immediately after the server confirms it, then
      // reconcile the badge and shared unmatched caches with server truth.
      reactQueryClient.setQueryData<EmailNotification[]>(
        ['/api/email-notifications', { unactionedOnly: true }],
        (current) => current?.filter((notification) => notification.id !== notificationId),
      );
      scheduleNotificationsReconcile();
      toast({
        title: "Email ignored",
        description: "This match was ignored for everyone. Approvals were removed, the email was retained, and it will stay out of unmatched emails.",
      });
    },
    onError: (error: unknown) => {
      const { body } = parseApiErrorObject(error);
      const message =
        (body && typeof body === 'object' && (body.error || body.message)) ||
        (error instanceof Error ? error.message : null) ||
        "Failed to ignore this email match.";
      // Do not remove or close the row on failure: the admin can retry the
      // same global action after the error has been surfaced.
      toast({
        title: "Error",
        description: String(message),
        variant: "destructive",
      });
    },
    onSettled: (_data, _error, notificationId) => {
      inflightIgnoreIds.current.delete(notificationId);
    },
  });

  const ignoreUnmatchedEmailMutation = useMutation({
    mutationFn: async ({
      emailAccountId,
      emailUID,
    }: {
      emailAccountId: string;
      emailUID: number;
    }) => {
      const response = await apiRequest(
        'POST',
        '/api/email-notifications/unmatched/ignore',
        { emailAccountId, emailUID },
      );
      return response.json() as Promise<IgnoreEmailResponse>;
    },
    onSuccess: (_data, variables) => {
      removeUnmatchedEmailFromLocalCaches(variables.emailAccountId, variables.emailUID);
      invalidateUnmatchedEmailQueries();
      toast({
        title: "Email ignored",
        description: "This email was ignored for everyone. It was retained and will stay out of unmatched emails.",
      });
    },
    onError: (error: unknown, variables) => {
      const { body } = parseApiErrorObject(error);
      if (body?.alreadyHandled === true) {
        removeUnmatchedEmailFromLocalCaches(variables.emailAccountId, variables.emailUID);
        invalidateUnmatchedEmailQueries();
        toast({
          title: "Already handled",
          description: "This email was already handled by another reviewer.",
        });
        return;
      }
      const message =
        (body && typeof body === 'object' && (body.error || body.message)) ||
        (error instanceof Error ? error.message : null) ||
        "Failed to ignore this unmatched email.";
      toast({
        title: "Error",
        description: String(message),
        variant: "destructive",
      });
    },
    onSettled: (_data, _error, variables) => {
      if (!variables) return;
      inflightUnmatchedIgnoreIds.current.delete(
        `${variables.emailAccountId}:${variables.emailUID}`,
      );
    },
  });

  const dontKnowUnmatchedEmailMutation = useMutation({
    mutationFn: async ({
      emailAccountId,
      emailUID,
    }: {
      emailAccountId: string;
      emailUID: number;
    }) => {
      const response = await apiRequest(
        'POST',
        '/api/email-notifications/unmatched/dont-know',
        { emailAccountId, emailUID },
      );
      return response.json() as Promise<{ status: 'hidden' | 'forwarded_admin' }>;
    },
    onSuccess: (data, variables) => {
      // Hide this row locally only after the server confirms the answer. A
      // successful forwarded_admin response also removes it globally through
      // the invalidation below.
      const id = `${variables.emailAccountId}:${variables.emailUID}`;
      setHiddenUnmatchedIds((current) => new Set(current).add(id));
      invalidateUnmatchedEmailQueries();
      toast({
        title: data.status === 'forwarded_admin' ? 'Forwarded to Admin' : 'Response recorded',
        description: data.status === 'forwarded_admin'
          ? 'All selected reviewers have answered. Admins will decide this email.'
          : 'This email is hidden from your unmatched pool.',
      });
    },
    onError: (error: unknown, variables) => {
      const { body } = parseApiErrorObject(error);
      if (body?.alreadyHandled === true) {
        const id = `${variables.emailAccountId}:${variables.emailUID}`;
        setHiddenUnmatchedIds((current) => new Set(current).add(id));
        invalidateUnmatchedEmailQueries();
        toast({
          title: 'Already handled',
          description: 'This email was already handled by another reviewer.',
        });
        return;
      }
      const message =
        (body && typeof body === 'object' && (body.error || body.message)) ||
        (error instanceof Error ? error.message : null) ||
        "Failed to record “I don't know”.";
      toast({
        title: 'Error',
        description: String(message),
        variant: 'destructive',
      });
    },
  });

  const forwardUnmatchedToAdminMutation = useMutation({
    mutationFn: async ({
      emailAccountId,
      emailUID,
    }: {
      emailAccountId: string;
      emailUID: number;
    }) => {
      const response = await apiRequest(
        'POST',
        '/api/email-notifications/unmatched/forward-admin',
        { emailAccountId, emailUID },
      );
      return response.json() as Promise<{ status: 'hidden' | 'forwarded_admin' }>;
    },
    onSuccess: (_data, variables) => {
      const id = `${variables.emailAccountId}:${variables.emailUID}`;
      setHiddenUnmatchedIds((current) => new Set(current).add(id));
      invalidateUnmatchedEmailQueries();
      toast({
        title: 'Forwarded to Admin',
        description: 'This email was removed from the shared pool and sent to admins.',
      });
    },
    onError: (error: unknown, variables) => {
      const { body } = parseApiErrorObject(error);
      if (body?.alreadyHandled === true) {
        const id = `${variables.emailAccountId}:${variables.emailUID}`;
        setHiddenUnmatchedIds((current) => new Set(current).add(id));
        invalidateUnmatchedEmailQueries();
        toast({
          title: 'Already handled',
          description: 'This email was already handled by another reviewer.',
        });
        return;
      }
      const message =
        (body && typeof body === 'object' && (body.error || body.message)) ||
        (error instanceof Error ? error.message : null) ||
        'Failed to forward this email to admins.';
      toast({
        title: 'Error',
        description: String(message),
        variant: 'destructive',
      });
    },
  });

  // Email notification action handlers
  const handleConfirmEmail = (e: React.MouseEvent, notificationId: number, reply = false) => {
    e.stopPropagation();
    // Drop duplicate clicks for the same notification while one is in flight.
    if (inflightConfirmIds.current.has(notificationId)) {
      return;
    }
    inflightConfirmIds.current.add(notificationId);
    if (reply) replyAfterConfirmIds.current.add(notificationId);
    confirmEmailMutation.mutate(notificationId);
  };

  const handleForwardEmail = (e: React.MouseEvent, notificationId: number) => {
    e.stopPropagation();
    forwardEmailMutation.mutate(notificationId);
  };

  const handleDontKnowEmail = (e: React.MouseEvent, notificationId: number) => {
    e.stopPropagation();
    if (
      dontKnowEmailMutation.isPending &&
      dontKnowEmailMutation.variables === notificationId
    ) {
      return;
    }
    dontKnowEmailMutation.mutate(notificationId);
  };

  const handleOpenReassignDialog = (e: React.MouseEvent, notification: EmailNotification) => {
    e.stopPropagation();
    setSelectedReassignEmail({ kind: "personal", item: notification });
    setReassignDialogOpen(true);
  };

  const handleIgnoreEmail = (e: React.MouseEvent, notificationId: number) => {
    e.stopPropagation();
    if (ignoreEmailMutation.isPending) return;
    if (inflightIgnoreIds.current.has(notificationId)) return;
    inflightIgnoreIds.current.add(notificationId);
    ignoreEmailMutation.mutate(notificationId);
  };

  const handleOpenUnmatchedReassignDialog = (e: React.MouseEvent, email: UnmatchedEmail) => {
    e.stopPropagation();
    setSelectedReassignEmail({ kind: "unmatched", item: email });
    setReassignDialogOpen(true);
  };

  const handleUnmatchedDontKnow = (e: React.MouseEvent, email: UnmatchedEmail) => {
    e.stopPropagation();
    if (dontKnowUnmatchedEmailMutation.isPending || forwardUnmatchedToAdminMutation.isPending) return;
    dontKnowUnmatchedEmailMutation.mutate({
      emailAccountId: email.emailAccountId,
      emailUID: email.emailUID,
    });
  };

  const handleForwardUnmatchedToAdmin = (e: React.MouseEvent, email: UnmatchedEmail) => {
    e.stopPropagation();
    if (dontKnowUnmatchedEmailMutation.isPending || forwardUnmatchedToAdminMutation.isPending) return;
    forwardUnmatchedToAdminMutation.mutate({
      emailAccountId: email.emailAccountId,
      emailUID: email.emailUID,
    });
  };

  const handleReassignSubmit = (projectId: string) => {
    if (selectedReassignEmail?.kind === "personal") {
      reassignEmailMutation.mutate({
        notificationId: selectedReassignEmail.item.id,
        projectId
      });
    } else if (selectedReassignEmail?.kind === "unmatched") {
      reassignUnmatchedEmailMutation.mutate({
        emailAccountId: selectedReassignEmail.item.emailAccountId,
        emailUID: selectedReassignEmail.item.emailUID,
        projectId,
      });
    }
  };

  const handleIgnoreUnmatchedEmail = (e: React.MouseEvent, email: UnmatchedEmail) => {
    e.stopPropagation();
    const emailKey = `${email.emailAccountId}:${email.emailUID}`;
    if (inflightUnmatchedIgnoreIds.current.has(emailKey)) {
      return;
    }
    inflightUnmatchedIgnoreIds.current.add(emailKey);
    ignoreUnmatchedEmailMutation.mutate({
      emailAccountId: email.emailAccountId,
      emailUID: email.emailUID,
    });
  };

  const resetReassignDialog = () => {
    setReassignDialogOpen(false);
    setSelectedReassignEmail(null);
    setProjectSearchQuery("");
  };

  const toggleEmailExpansion = (notificationId: number) => {
    setExpandedEmailIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(notificationId)) {
        newSet.delete(notificationId);
      } else {
        newSet.add(notificationId);
      }
      return newSet;
    });
  };

  const toggleUnmatchedExpansion = (emailAccountId: string, emailUID: number) => {
    const id = `${emailAccountId}:${emailUID}`;
    setExpandedUnmatchedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const parseEmailBody = (emailBody: string | null): string => {
    if (!emailBody) return 'No email body available';

    // Convert HTML block boundaries to newlines BEFORE stripping tags —
    // otherwise textContent collapses everything into one wall of text.
    const withBreaks = emailBody
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|li|tr|h[1-6]|blockquote|pre)>/gi, '\n')
      .replace(/<\/?(ul|ol)>/gi, '\n');

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = withBreaks;

    let text = tempDiv.textContent || tempDiv.innerText || '';

    text = text
      .replace(/\r\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/[ \t]+/g, ' ')
      .replace(/[ \t]*\n[ \t]*/g, '\n')
      .trim();

    return text || 'No content';
  };

  const formatEmailDate = (dateString: string): string => {
    const emailDate = new Date(dateString);
    const now = new Date();
    const hoursDiff = (now.getTime() - emailDate.getTime()) / (1000 * 60 * 60);
    
    // Show relative time for emails within the last 24 hours
    if (hoursDiff < 24) {
      return `${formatDistanceToNow(emailDate, { addSuffix: true })} · ${format(emailDate, 'HH:mm')}`;
    }
    
    // Show absolute date for older emails
    return format(emailDate, 'MMM d, yyyy HH:mm');
  };

  // Filter and sort email notifications
  const sortedEmailNotifications = [...emailNotifications]
    .filter(notification => !notification.isActioned)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  // Filter projects for reassignment dialog based on search query
  const filteredProjects = allProjects.filter(project =>
    project.id.toLowerCase().includes(projectSearchQuery.toLowerCase()) ||
    project.projectName.toLowerCase().includes(projectSearchQuery.toLowerCase())
  );
  const isReassignPending = reassignEmailMutation.isPending || reassignUnmatchedEmailMutation.isPending;
  const isDontKnowAllowed = (notification: EmailNotification) =>
    (notification.detectionMethod === 'thread' ||
      notification.detectionMethod === 'subject' ||
      notification.detectionMethod === 'pool') &&
    !notification.flagReason &&
    notification.project?.communicationMode !== 'admin_only';
  const showApprovalExplanation =
    selectedReassignEmail?.kind === "unmatched" ||
    (selectedReassignEmail?.kind === "personal" &&
      (selectedReassignEmail.item.detectionMethod === "pool" ||
        selectedReassignEmail.item.detectionMethod === "pool_admin"));

  // Handle dropdown open/close with manual refetch as fallback
  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen);
    
    // When opening the dropdown, manually refetch to ensure fresh data
    if (newOpen) {
      refetchEmailNotifications();
      if (canViewUnmatched) {
        refetchUnmatched();
      }
    }
  };

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="relative navy-card border-gray-600 text-gray-300 hover:bg-gray-700"
            data-testid="button-email-notification"
          >
            <Mail className="w-4 h-4" />
            {unactionedCount > 0 && (
              <span 
                className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-purple-500 rounded-full"
                data-testid="badge-email-count"
              >
                {unactionedCount > 99 ? '99+' : unactionedCount}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end" 
          className="w-[720px] max-w-[calc(100vw-24px)] bg-gray-800 border-gray-600 max-h-[min(780px,80vh)] overflow-y-auto"
        >
          {showUnmatchedSection && (
            <>
              <DropdownMenuLabel className="text-gray-300">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    {unmatchedParticipating ? (
                      <Mail className="w-4 h-4" />
                    ) : (
                      <Settings className="w-4 h-4" />
                    )}
                    {unmatchedParticipating
                      ? `Unmatched emails (${unmatchedTotal})`
                      : "Unmatched email reviewers"}
                  </span>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs text-gray-300 hover:bg-gray-700"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsOpen(false);
                        setUnmatchedSettingsOpen(true);
                      }}
                      data-testid="button-unmatched-email-settings"
                      aria-label="Unmatched email settings"
                    >
                      <Settings className="mr-1 h-3.5 w-3.5" />
                      Settings
                    </Button>
                  )}
                </div>
                <p className="mt-1 text-xs font-normal text-gray-400">
                  {unmatchedParticipating
                    ? "Incoming emails without a matched project need a project before they can be added to project activity."
                    : "Manage which internal users review unmatched incoming emails."}
                </p>
                {isAdmin && !unmatchedParticipating && (
                  <p className="mt-1 text-xs font-normal text-gray-400">
                    You are not selected as a reviewer. Use Settings to manage the shared reviewer pool.
                  </p>
                )}
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-gray-700" />

              {!unmatchedParticipating && isAdmin ? (
                <div className="p-4 text-center text-gray-400" data-testid="unmatched-not-participating">
                  You are not a participating reviewer for unmatched emails.
                </div>
              ) : isLoadingUnmatched ? (
                <div className="p-4 text-center text-gray-400" data-testid="unmatched-loading">
                  Loading unmatched emails...
                </div>
              ) : isUnmatchedError ? (
                <div className="p-4 text-center text-red-300" data-testid="unmatched-error">
                  <p>Unable to load unmatched emails.</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2 text-gray-300 hover:bg-gray-700"
                    onClick={() => refetchUnmatched()}
                    data-testid="button-retry-unmatched"
                  >
                    Retry
                  </Button>
                </div>
              ) : visibleUnmatchedEmails.length === 0 ? (
                <div className="p-4 text-center text-gray-400" data-testid="unmatched-empty">
                  No unmatched incoming emails
                </div>
              ) : (
                <div className="max-h-[360px] overflow-y-auto">
                  {visibleUnmatchedEmails.map((email) => {
                    const unmatchedId = `${email.emailAccountId}:${email.emailUID}`;
                    const sender = email.emailData.fromName || email.emailData.fromAddress;
                    const hasCC = (email.emailData.ccAddresses || []).length > 0;
                    const hasEmailBody = !!email.emailData.emailBody;
                    const isExpanded = expandedUnmatchedIds.has(unmatchedId);
                    const ownReviewer = email.reviewers?.find((reviewer) => reviewer.id === user?.id);
                    const isActionPending =
                      dontKnowUnmatchedEmailMutation.isPending ||
                      forwardUnmatchedToAdminMutation.isPending ||
                      ignoreUnmatchedEmailMutation.isPending;

                    return (
                      <DropdownMenuItem
                        key={unmatchedId}
                        className="flex flex-col gap-2 p-3 cursor-default bg-amber-500/10 hover:bg-amber-500/20 border-l-2 border-amber-500"
                        onSelect={(e) => e.preventDefault()}
                        data-testid={`unmatched-email-${email.emailAccountId}-${email.emailUID}`}
                      >
                        <div
                          className="flex items-start justify-between gap-2 w-full cursor-pointer"
                          onClick={() => hasEmailBody && toggleUnmatchedExpansion(email.emailAccountId, email.emailUID)}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-amber-300 line-clamp-1">
                              {email.emailData.subject}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              From: {sender}
                            </p>
                            {hasCC && (
                              <p className="text-xs text-gray-400">
                                CC: {email.emailData.ccAddresses.join(', ')}
                              </p>
                            )}
                            <p className="text-xs text-gray-300 font-medium mt-1">
                              {formatEmailDate(email.emailData.emailDate)}
                            </p>
                          </div>
                          {hasEmailBody && (
                            <button
                              className="text-gray-400 hover:text-gray-300 p-1"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleUnmatchedExpansion(email.emailAccountId, email.emailUID);
                              }}
                              data-testid={`button-toggle-unmatched-email-${email.emailAccountId}-${email.emailUID}`}
                              aria-label={isExpanded ? "Hide email preview" : "Show email preview"}
                            >
                              {isExpanded ? (
                                <ChevronDown className="w-4 h-4" />
                              ) : (
                                <ChevronRight className="w-4 h-4" />
                              )}
                            </button>
                          )}
                        </div>

                        <UnmatchedEmailParticipantChips
                          reviewers={email.reviewers}
                          emailId={`${email.emailAccountId}-${email.emailUID}`}
                        />

                        <div className="flex flex-wrap items-center gap-2 w-full pt-2 border-t border-gray-700">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 flex-1"
                            onClick={(e) => handleOpenUnmatchedReassignDialog(e, email)}
                            title="Choose the correct project for this email. If you are not a project member, its team will be asked to approve."
                            disabled={reassignUnmatchedEmailMutation.isPending}
                            data-testid={`button-reassign-unmatched-email-${email.emailAccountId}-${email.emailUID}`}
                          >
                            <FolderSearch className="w-3 h-3 mr-1" />
                            Reassign
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs text-gray-300 hover:bg-gray-700 flex-1"
                            onClick={(e) => handleUnmatchedDontKnow(e, email)}
                            title="Let the other reviewers identify the project and hide this email from your list. If nobody knows, it goes to admins."
                            disabled={
                              isActionPending ||
                              (!!email.reviewers && !!user && ownReviewer?.status === "dont_know")
                            }
                            data-testid={`button-dont-know-unmatched-email-${email.emailAccountId}-${email.emailUID}`}
                          >
                            {dontKnowUnmatchedEmailMutation.isPending &&
                            dontKnowUnmatchedEmailMutation.variables?.emailAccountId === email.emailAccountId &&
                            dontKnowUnmatchedEmailMutation.variables?.emailUID === email.emailUID
                              ? "Saving..."
                              : "I don't know"}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs text-red-300 hover:text-red-200 hover:bg-red-500/10 flex-1"
                            onClick={(e) => handleForwardUnmatchedToAdmin(e, email)}
                            title="Send this email to admins to decide where it belongs and remove it from the team's unmatched list."
                            disabled={isActionPending}
                            data-testid={`button-forward-admin-unmatched-email-${email.emailAccountId}-${email.emailUID}`}
                          >
                            <Forward className="w-3 h-3 mr-1" />
                            {forwardUnmatchedToAdminMutation.isPending &&
                            forwardUnmatchedToAdminMutation.variables?.emailAccountId === email.emailAccountId &&
                            forwardUnmatchedToAdminMutation.variables?.emailUID === email.emailUID
                              ? "Forwarding..."
                              : "Forward to Admin"}
                          </Button>
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs text-red-300 hover:text-red-200 hover:bg-red-500/10 flex-1"
                              onClick={(e) => handleIgnoreUnmatchedEmail(e, email)}
                              title="Ignore this email for everyone. Retains the email, retires pending approvals, and keeps it out of unmatched emails."
                              disabled={
                                ignoreUnmatchedEmailMutation.isPending &&
                                ignoreUnmatchedEmailMutation.variables?.emailAccountId === email.emailAccountId &&
                                ignoreUnmatchedEmailMutation.variables?.emailUID === email.emailUID
                              }
                              data-testid={`button-ignore-unmatched-email-${email.emailAccountId}-${email.emailUID}`}
                            >
                              <XCircle className="w-3 h-3 mr-1" />
                              {ignoreUnmatchedEmailMutation.isPending &&
                              ignoreUnmatchedEmailMutation.variables?.emailAccountId === email.emailAccountId &&
                              ignoreUnmatchedEmailMutation.variables?.emailUID === email.emailUID
                                ? "Ignoring..."
                                : "Ignore"}
                            </Button>
                          )}
                        </div>
                        {/* Keep decisions above the independently scrolling preview:
                            a long body must not push them below the list viewport. */}
                        {isExpanded && hasEmailBody && (
                          <div
                            className="w-full pt-2 border-t border-gray-700/50"
                            data-testid={`unmatched-email-preview-${email.emailAccountId}-${email.emailUID}`}
                          >
                            <div className="max-h-[320px] overflow-y-auto bg-gray-900/50 rounded p-3">
                              <p className="text-xs text-gray-300 whitespace-pre-wrap break-words leading-relaxed">
                                {parseEmailBody(email.emailData.emailBody)}
                              </p>
                            </div>
                          </div>
                        )}
                      </DropdownMenuItem>
                    );
                  })}
                  {hasMoreUnmatched && (
                    <div className="p-2 border-t border-gray-700">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-xs text-gray-300 hover:bg-gray-700"
                        onClick={() => fetchNextUnmatchedPage()}
                        disabled={isFetchingNextUnmatchedPage}
                        data-testid="button-load-more-unmatched"
                      >
                        {isFetchingNextUnmatchedPage ? "Loading older emails..." : "Load more older emails"}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {showUnmatchedSection && <DropdownMenuSeparator className="bg-gray-700" />}
          <DropdownMenuLabel className="flex items-center justify-between text-gray-300">
            <span className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email Matches ({sortedEmailNotifications.length})
              <SoundSelector soundType="soundEmailMatches" label="" />
            </span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-gray-700" />
          
          {isLoadingEmails ? (
            <div className="p-4 text-center text-gray-400">
              Loading email notifications...
            </div>
          ) : isEmailNotificationsError ? (
            <div className="p-4 text-center text-red-300" data-testid="email-notifications-error">
              <p>Unable to load email notifications.</p>
              <Button
                variant="ghost"
                size="sm"
                className="mt-2 text-gray-300 hover:bg-gray-700"
                onClick={() => refetchEmailNotifications()}
                data-testid="button-retry-email-notifications"
              >
                Retry
              </Button>
            </div>
          ) : sortedEmailNotifications.length === 0 ? (
            <div className="p-4 text-center text-gray-400">
              No pending email matches
            </div>
          ) : (
            <div className="max-h-[680px] overflow-y-auto">
              {sortedEmailNotifications.map((notification) => {
                const sender = notification.emailData.fromName || notification.emailData.fromAddress;
                const hasCC = notification.emailData.ccAddresses.length > 0;
                const isExpanded = expandedEmailIds.has(notification.id);
                const hasEmailBody = !!notification.emailData.emailBody;
                
                // Get flag reason badge details
                const getFlagBadge = () => {
                  if (!notification.flagReason) return null;
                  
                  const flagConfig = {
                    nonexistent_project: { label: 'Invalid Project', color: 'bg-orange-500' },
                    blacklist_admin: { label: 'Admin Blacklist', color: 'bg-red-500' },
                    blacklist_chatgpt: { label: 'ChatGPT Blacklist', color: 'bg-red-500' },
                    chatgpt_rejected: { label: 'ChatGPT Rejected', color: 'bg-orange-500' },
                  };
                  
                  const config = flagConfig[notification.flagReason as keyof typeof flagConfig];
                  return config ? (
                    <Badge className={cn("text-xs", config.color, "text-white border-0")}>
                      <AlertCircle className="w-3 h-3 mr-1" />
                      {config.label}
                    </Badge>
                  ) : null;
                };
                
                // Get detection method badge
                const getDetectionBadge = () => {
                  if (!notification.detectionMethod) return null;
                   if (notification.detectionMethod === 'pool_admin') {
                     return (
                       <Badge className="text-xs bg-red-600 text-white border-0">
                         Needs admin decision
                       </Badge>
                     );
                   }
                  
                  return notification.detectionMethod === 'thread' ? (
                    <Badge className="text-xs bg-green-500 text-white border-0">
                      Thread
                    </Badge>
                  ) : (
                    <Badge className="text-xs bg-blue-500 text-white border-0">
                      Subject
                    </Badge>
                  );
                };
                 const isAdminEscalation = notification.detectionMethod === 'pool_admin';
                
                return (
                  <DropdownMenuItem
                    key={notification.id}
                     className={cn(
                       "flex flex-col gap-2 p-3 cursor-default border-l-2",
                       isAdminEscalation
                         ? "bg-red-500/10 hover:bg-red-500/20 border-red-500"
                         : "bg-purple-500/10 hover:bg-purple-500/20 border-purple-500",
                     )}
                    onSelect={(e) => e.preventDefault()}
                    data-testid={`email-notification-${notification.id}`}
                  >
                    <div 
                      className="flex items-start justify-between gap-2 w-full cursor-pointer"
                      onClick={() => hasEmailBody && toggleEmailExpansion(notification.id)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          {getDetectionBadge()}
                          {getFlagBadge()}
                        </div>
                        {isAdmin && isAdminEscalation && (
                          <p
                            className="text-sm font-semibold text-red-200 mb-1"
                            data-testid={`admin-forward-provenance-${notification.id}`}
                          >
                            {notification.forwardedByFirstName
                              ? <>Forwarded by {notification.forwardedByFirstName}</>
                              : notification.adminEscalationReason === 'all_declined'
                                ? 'Forwarded automatically · all selected reviewers said “I don’t know”'
                                : notification.adminEscalationReason === 'no_reviewers'
                                  ? 'Forwarded automatically · no reviewers are selected'
                                  : notification.adminEscalationReason === 'settings'
                                    ? 'Forwarded automatically · reviewer settings changed'
                                    : notification.adminEscalationReason === 'historical_uncached'
                                      ? 'Older mail found in the mailbox · never reviewed before, admin check only'
                                      : notification.adminEscalationReason === 'historical_pending'
                                        ? 'Historical intake record · restored for admin review'
                                        : 'Forwarded to admins'}
                          </p>
                        )}
                       <p className={cn(
                         "text-sm font-medium line-clamp-1",
                         isAdminEscalation ? "text-red-300" : "text-purple-400",
                       )}>
                          {notification.emailData.subject}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          From: {sender}
                        </p>
                        {hasCC && (
                          <p className="text-xs text-gray-400">
                            CC: {notification.emailData.ccAddresses.join(', ')}
                          </p>
                        )}
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-xs text-gray-300 font-medium">
                            {formatEmailDate(notification.emailData.emailDate)}
                          </p>
                           {notification.project && (
                             <p className={cn(
                               "text-xs font-medium",
                               isAdminEscalation ? "text-red-300" : "text-purple-400",
                             )}>
                              → {notification.project.id}
                            </p>
                          )}
                        </div>
                      </div>
                      {hasEmailBody && (
                        <button
                          className="text-gray-400 hover:text-gray-300 p-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleEmailExpansion(notification.id);
                          }}
                          data-testid={`button-toggle-email-${notification.id}`}
                          title={isExpanded ? "Hide the email preview." : "Show the email preview."}
                          aria-label={isExpanded ? "Hide the email preview" : "Show the email preview"}
                        >
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>
                    
                    {/* Email body preview - shown when expanded */}
                    {isExpanded && hasEmailBody && (
                      <div className="w-full pt-2 border-t border-gray-700/50">
                        <div className="max-h-[320px] overflow-y-auto bg-gray-900/50 rounded p-3">
                          <p className="text-xs text-gray-300 whitespace-pre-wrap break-words leading-relaxed">
                            {parseEmailBody(notification.emailData.emailBody)}
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {/* Action buttons */}
                    <div className="flex flex-wrap items-center gap-2 w-full pt-2 border-t border-gray-700">
                       {!isAdminEscalation && (
                         <Button
                           variant="ghost"
                           size="sm"
                           className="h-7 text-xs text-green-400 hover:text-green-300 hover:bg-green-500/10 flex-1"
                           onClick={(e) => handleConfirmEmail(e, notification.id)}
                           title="Approve the suggested project and add this email to its activity."
                           disabled={confirmEmailMutation.isPending && confirmEmailMutation.variables === notification.id}
                           data-testid={`button-confirm-email-${notification.id}`}
                         >
                           <CheckCircle className="w-3 h-3 mr-1" />
                           {confirmEmailMutation.isPending && confirmEmailMutation.variables === notification.id ? 'Confirming...' : 'Confirm'}
                         </Button>
                       )}
                       {!isAdminEscalation && (
                         <Button
                           variant="ghost"
                           size="sm"
                           className="h-7 text-xs text-green-400 hover:text-green-300 hover:bg-green-500/10 flex-1 whitespace-nowrap"
                           onClick={(e) => handleConfirmEmail(e, notification.id, true)}
                           title="Confirm this email's project and open a reply. Nothing is sent automatically."
                           disabled={confirmEmailMutation.isPending && confirmEmailMutation.variables === notification.id}
                           data-testid={`button-confirm-reply-email-${notification.id}`}
                         >
                           Confirm and Reply
                         </Button>
                       )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 flex-1"
                        onClick={(e) => handleOpenReassignDialog(e, notification)}
                        title="Choose a different project for this email."
                        disabled={reassignEmailMutation.isPending && reassignEmailMutation.variables?.notificationId === notification.id}
                        data-testid={`button-reassign-email-${notification.id}`}
                      >
                        <FolderSearch className="w-3 h-3 mr-1" />
                        Reassign
                      </Button>
                       {isDontKnowAllowed(notification) && (
                         <Button
                           variant="ghost"
                           size="sm"
                           className="h-7 text-xs text-gray-300 hover:bg-gray-700 flex-1"
                           onClick={(e) => handleDontKnowEmail(e, notification.id)}
                           title="Send this email back to the team to identify the correct project, with your 'I don't know' response already recorded. If no reviewers remain, it goes to admins."
                           disabled={
                             dontKnowEmailMutation.isPending &&
                             dontKnowEmailMutation.variables === notification.id
                           }
                           data-testid={`button-dont-know-email-${notification.id}`}
                         >
                           {dontKnowEmailMutation.isPending &&
                           dontKnowEmailMutation.variables === notification.id
                             ? "Saving..."
                             : "I don't know"}
                         </Button>
                       )}
                      {!isAdminEscalation && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-orange-400 hover:text-orange-300 hover:bg-orange-500/10 flex-1"
                        onClick={(e) => handleForwardEmail(e, notification.id)}
                        title="Forward this email to admins for review."
                        disabled={forwardEmailMutation.isPending && forwardEmailMutation.variables === notification.id}
                        data-testid={`button-forward-email-${notification.id}`}
                      >
                        <Forward className="w-3 h-3 mr-1" />
                        {forwardEmailMutation.isPending && forwardEmailMutation.variables === notification.id ? 'Forwarding...' : 'Forward to Admin'}
                      </Button>
                      )}
                       {isAdmin && (
                         <Button
                           variant="ghost"
                           size="sm"
                           className="h-7 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 flex-1"
                            onClick={(e) => handleIgnoreEmail(e, notification.id)}
                            title="Global final ignore: removes this match and everyone's approvals, keeps it out of unmatched emails, and does not delete the email."
                           disabled={ignoreEmailMutation.isPending && ignoreEmailMutation.variables === notification.id}
                           data-testid={`button-ignore-email-${notification.id}`}
                         >
                           <XCircle className="w-3 h-3 mr-1" />
                           {ignoreEmailMutation.isPending && ignoreEmailMutation.variables === notification.id ? 'Ignoring...' : 'Ignore'}
                         </Button>
                       )}
                    </div>
                  </DropdownMenuItem>
                );
              })}
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {isAdmin && (
        <UnmatchedEmailSettingsDialog
          open={unmatchedSettingsOpen}
          onOpenChange={setUnmatchedSettingsOpen}
          onSaved={() => {
            refetchUnmatched();
          }}
        />
      )}

      {/* Reassignment Dialog */}
      <Dialog open={reassignDialogOpen} onOpenChange={setReassignDialogOpen}>
        <DialogContent 
          className="bg-gray-800 border-gray-600 text-gray-300 max-w-2xl"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-gray-200">
              Reassign Email to Project
            </DialogTitle>
          </DialogHeader>
          
          {selectedReassignEmail && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-700 rounded-md">
                <p className="text-sm font-medium text-purple-400 mb-1">
                  {selectedReassignEmail.item.emailData.subject}
                </p>
                <p className="text-xs text-gray-400">
                  From: {selectedReassignEmail.item.emailData.fromName || selectedReassignEmail.item.emailData.fromAddress}
                </p>
                {selectedReassignEmail.kind === "personal" && selectedReassignEmail.item.project && (
                  <p className="text-xs text-gray-400 mt-1">
                    Currently: {selectedReassignEmail.item.project.id} - {selectedReassignEmail.item.project.projectName}
                  </p>
                )}
                {selectedReassignEmail.kind === "unmatched" && (
                  <p className="text-xs text-gray-400 mt-1">
                    This unmatched email needs a project before it can be added to project activity.
                  </p>
                )}
                {showApprovalExplanation && (
                  <p className="text-xs text-gray-400 mt-2">
                    If you are assigned to this project, a pulse is created immediately. Otherwise its team will be asked to approve.
                  </p>
                )}
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-300 mb-2 block">
                  Search Projects
                </label>
                <Input
                  type="text"
                  placeholder="Search by project ID or name..."
                  value={projectSearchQuery}
                  onChange={(e) => setProjectSearchQuery(e.target.value)}
                  className="bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400"
                  data-testid="input-project-search"
                />
              </div>
              
              <div className="max-h-[300px] overflow-y-auto space-y-1 border border-gray-700 rounded-md p-2">
                {filteredProjects.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">
                    No projects found
                  </p>
                ) : (
                  filteredProjects.map((project) => (
                    <button
                      key={project.id}
                      onClick={() => handleReassignSubmit(project.id)}
                      disabled={isReassignPending}
                      className={cn(
                        "w-full flex items-center gap-3 text-left p-2 rounded hover:bg-gray-700 transition-colors",
                        "text-sm text-gray-300",
                        isReassignPending && "opacity-50 cursor-not-allowed"
                      )}
                      data-testid={`project-option-${project.id}`}
                    >
                      <ProjectSuggestionThumbnail src={project.thumbnail} />
                      <span className="min-w-0 break-words">
                        <span className="font-medium text-purple-400">{project.id}</span>
                        <span className="text-gray-400"> - {project.projectName}</span>
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            {selectedReassignEmail?.kind === "personal" &&
              isDontKnowAllowed(selectedReassignEmail.item) && (
                <Button
                  variant="ghost"
                  onClick={(e) => handleDontKnowEmail(e, selectedReassignEmail.item.id)}
                  title="Send this email back to the team to identify the correct project, with your 'I don't know' response already recorded. If no reviewers remain, it goes to admins."
                  disabled={
                    dontKnowEmailMutation.isPending &&
                    dontKnowEmailMutation.variables === selectedReassignEmail.item.id
                  }
                  className="text-gray-300 hover:bg-gray-700"
                  data-testid={`button-dont-know-reassign-email-${selectedReassignEmail.item.id}`}
                >
                  {dontKnowEmailMutation.isPending &&
                  dontKnowEmailMutation.variables === selectedReassignEmail.item.id
                    ? "Saving..."
                    : "I don't know"}
                </Button>
              )}
            <Button
              variant="outline"
              onClick={resetReassignDialog}
              title="Close this window without changing the email's project."
              disabled={isReassignPending}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
              data-testid="button-cancel-reassign"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {confirmedReply && (
        <MailOutDialog
          open
          onOpenChange={(open) => { if (!open) setConfirmedReply(null); }}
          projectId={confirmedReply.projectId}
          composeLabel="Reply"
          replySource={{ pulseId: confirmedReply.pulseId }}
        />
      )}
    </>
  );
}
